import classNames from 'classnames';
import React, { useState } from 'react';
import { useMutateCard } from '../../../hooks/apolloHooks/useMutateCard';
import { validateCardName } from '../../../utils/cardValidationSchemas';
import { CheckmarkIcon } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import RightsWrapper from '../RightsWrapper';

type CardNameEditorProps = {
	cardId: string
	currentName: string
	isCreditCard?: boolean
	isCompany?: boolean
	maskedPan?: string
	onUpdateSuccess?: (newName?: string) => void
}

const CardNameEditor: React.FC<CardNameEditorProps> = ({ cardId, currentName, isCreditCard, isCompany, maskedPan, onUpdateSuccess }) => {

	const [isEditing, setIsEditing] = useState(false)
	const [newName, setNewName] = useState(currentName || '')
	const [validationError, setValidationError] = useState<string | null>(null)
	const [successMessage, setSuccessMessage] = useState<string | null>(null)
	const { updateCardReferences, loading, error } = useMutateCard()

	const handleEditClick = () => {
		setNewName(currentName || '')
		setValidationError(null)
		setIsEditing(true)
	}

	const handleCancel = () => {
		setNewName(currentName || '')
		setValidationError(null)
		setIsEditing(false)
	}

	const trimmedPan = maskedPan?.replace(/[* ]/g, '')
	const identifierReference = isCreditCard ?
		(isCompany ?
			'FÖRETAGSKORT, ' + trimmedPan
			: 'TANKKORT, ' + trimmedPan)
		: 'SMARTKORT, ' + trimmedPan

	const handleSave = async () => {
		try {
			// Validate input
			const validation = validateCardName(newName)
			if (!validation.valid) {
				setValidationError(validation.error || 'Felaktig inmatning')
				return
			}
			setValidationError(null)

			const result = await updateCardReferences({
				cardId: parseInt(cardId),
				reference1: identifierReference,
				reference2: newName
			})

			if (result.data) {
				setIsEditing(false)
				setSuccessMessage('Namn uppdaterat')
				// Delay refetch to allow success message to be shown, then refetch
				setTimeout(() => {
					if (onUpdateSuccess) {
						onUpdateSuccess(newName)
					}
				}, 2500) // 1.5 second delay before refetch
			}
		} catch (err) {
			console.error('Failed to update card name:', err)
			setValidationError('Kunde inte uppdatera kortnamn. Försök igen.')
		}
	}

	if (!isEditing) {
		return (
			<div className="card-name-editor">
				<div>
					{successMessage && (
						<p className="card-name-editor__success">
							<CheckmarkIcon />
							{successMessage}
						</p>
					)}
					<span className="disabled-input">{currentName || ''}</span>
				</div>
				<RightsWrapper
					disableButtonText={'Ändra kortnamn'}
					disableButtonClasses="disable-fill-color body-text"
				>
					<button
						type="button"
						className={classNames(
							'card-name-editor__edit-button button-edit body-text',
							{ 'invert': !isCreditCard }
						)}
						onClick={handleEditClick}
					>
						Ändra kortnamn
					</button>
				</RightsWrapper>

			</div>
		)
	}

	return (
		<div className="card-name-editor card-name-editor--editing">
			<label htmlFor={`card-name-${cardId}`} className="card-name-editor__label">
				Kortnamn
			</label>
			<input
				id={`card-name-${cardId}`}
				type="text"
				className="card-name-editor__input"
				value={newName}
				onChange={(e) => setNewName(e.target.value)}
				disabled={loading}
				placeholder="Ange kortnamn"
				maxLength={25}
			/>
			{validationError && (
				<p className="card-name-editor__error">
					{validationError}
				</p>
			)}
			{error && (
				<p className="card-name-editor__error">
					Kunde inte uppdatera kortnamn. Försök igen eller kontakta kundservice.
				</p>
			)}
			<div className="card-name-editor__actions">
				<Button
					className="body-text"
					type="button"
					onClick={handleSave}
					disabled={loading}
				>
					{loading ? 'Sparar...' : 'Spara'}
				</Button>
				<Button
					type="button"
					className={classNames(
						'button-cancel',
						'body-text',
						{ 'invert': isCreditCard }
					)}
					onClick={handleCancel}
					disabled={loading}
				>
					Avbryt
				</Button>
			</div>
		</div>
	)
}

export default CardNameEditor
